import React from 'react';

import cx from 'classnames';
import { Arrow, ToggleLayer } from 'react-laag';
import { useSelector } from 'react-redux';

import { User } from 'channels/types';
import Dropdown from 'components/shared/Dropdown';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { userSelector } from 'store/user/selectors';
import { ITINERARIES_ITEM_TYPE_TO_STEP_LABEL_MAP } from 'utils/itineraries/map';
import { TItineraryItemType } from 'utils/types';

import { useBubbleColors } from './functions';
import s from './index.module.css';

type Props = { activeUsers: User[]; itemType: TItineraryItemType };

const BUBBLE_DISPLAY_COUNT = 2;

const UserBubbles = ({ activeUsers, itemType }: Props) => {
  const currentUserId = useSelector(userSelector).id;
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { getUserColor } = useBubbleColors();

  const bubbleDisplayCount = isTabletOrMobile ? BUBBLE_DISPLAY_COUNT - 1 : BUBBLE_DISPLAY_COUNT;
  const displayedUsers = activeUsers.slice(0, bubbleDisplayCount);
  const otherUsers = activeUsers.slice(bubbleDisplayCount);

  if (activeUsers.length === 0) {
    return null;
  }

  return (
    <div className={s.wrapper}>
      {displayedUsers.map((user, index) => (
        <UserBubble
          key={user.userId + index}
          user={user}
          itemType={itemType}
          zIndex={displayedUsers.length - index + 1}
          backgroundColor={getUserColor(user.userId)}
          isShiftBubble={displayedUsers.length > 1}
          isCurrentUser={currentUserId === +user.userId}
        />
      ))}
      {otherUsers.length > 0 && (
        <OtherUsersBubble
          activeUsers={activeUsers}
          otherUsers={otherUsers}
          itemType={itemType}
          currentUserId={currentUserId}
          bubbleDisplayCount={bubbleDisplayCount}
        />
      )}
    </div>
  );
};

type UserBubbleLayoutProps = {
  layoutContent: () => React.ReactNode;
  dropdownContent: () => React.ReactNode;
};

const UserBubbleLayout = ({ layoutContent, dropdownContent }: UserBubbleLayoutProps) => {
  return (
    <ToggleLayer
      fixed
      container={() => document.querySelector('#root')!}
      renderLayer={({ isOpen, layerProps, arrowStyle, layerSide }) => {
        return (
          isOpen && (
            <div ref={layerProps.ref} className="layer" style={{ ...layerProps.style, marginTop: '8px', zIndex: 9999 }}>
              <Dropdown className={s.dropdown}>
                <Arrow
                  style={{ ...arrowStyle, height: '1.1rem', zIndex: '21' }}
                  layerSide={layerSide}
                  borderWidth={1}
                  borderColor="rgba(0, 0, 0, 0.04)"
                  backgroundColor="#fff"
                  angle={45}
                  roundness={0.5}
                  size={12}
                />
                {dropdownContent()}
              </Dropdown>
            </div>
          )
        );
      }}
      placement={{
        anchor: 'BOTTOM_CENTER',
        autoAdjust: true,
        triggerOffset: 10,
        possibleAnchors: ['TOP_CENTER'],
      }}
    >
      {({ triggerRef, open, close }) => {
        return (
          <div ref={triggerRef} onMouseEnter={open} onMouseLeave={close}>
            {layoutContent()}
          </div>
        );
      }}
    </ToggleLayer>
  );
};

type UserBubbleProps = {
  user: User;
  zIndex: number;
  itemType: Props['itemType'];
  backgroundColor: string;
  isShiftBubble: boolean;
  isCurrentUser: boolean;
};

const UserBubble = ({ user, zIndex, isShiftBubble, itemType, isCurrentUser, backgroundColor }: UserBubbleProps) => {
  const { sid, fullName, tab } = user;

  return (
    <div className={cx(s.userBubbleContainer, isShiftBubble && s.shiftedUserBubbleContainer)} style={{ zIndex }}>
      <UserBubbleLayout
        layoutContent={() => (
          <div className={s.bubble} key={sid} style={{ backgroundColor }}>
            {fullName?.[0].toUpperCase() || 'U'}
          </div>
        )}
        dropdownContent={() => (
          <div className={s.dropdownElement}>
            <div>{isCurrentUser ? 'You' : fullName}</div>
            <span>{`${ITINERARIES_ITEM_TYPE_TO_STEP_LABEL_MAP[itemType][tab]} Tab`}</span>
          </div>
        )}
      />
    </div>
  );
};

type OtherUsersBubbleProps = {
  activeUsers: Props['activeUsers'];
  otherUsers: Props['activeUsers'];
  itemType: Props['itemType'];
  currentUserId: number;
  bubbleDisplayCount: number;
};

const OtherUsersBubble = ({
  activeUsers,
  otherUsers,
  itemType,
  currentUserId,
  bubbleDisplayCount,
}: OtherUsersBubbleProps) => {
  return (
    <UserBubbleLayout
      layoutContent={() => (
        <div className={s.bubble} style={{ backgroundColor: '#E0E0E0' }}>
          +{activeUsers.length - bubbleDisplayCount}
        </div>
      )}
      dropdownContent={() => {
        return otherUsers.map(({ userId, fullName, tab }, index) => (
          <div key={index} className={s.dropdownElement}>
            <div>{currentUserId === +userId ? 'You' : fullName}</div>
            <span>{`${ITINERARIES_ITEM_TYPE_TO_STEP_LABEL_MAP[itemType][tab]} Tab`}</span>
          </div>
        ));
      }}
    />
  );
};

export default UserBubbles;
